(ns frontend.view.components.cropper
  (:require [basic-components.modal :as zap-modal]
            [cljsjs.react-easy-crop]
            [common.localization :as loc]
            [frontend.actions.common :as common-actions]
            [frontend.logging :as logging]
            [frontend.utils :as fu]
            [frontend.view.modals.dynamic.view :as dynamic-modal]
            [frontend.view.modals.events :as modals]
            [oops.core :as oops]
            [reagent.core :as r]
            [ui.color :as color]
            [ui.components.button.view :as button]))

(defn modal-content
  [{:keys [title content-text]}]
  [:div.container.p-5.text-center
   [:div
    [:img
     {:src "/img/image-is-too-small.svg"}]]
   [:h2.title-h2.mb-4 title]
   [:div
    content-text]])

(defn view
  [{:keys [state entity file-name attribute target-width target-height classes round? save-always? uploaded? close-modal!]}]
  (let [state (or state (r/atom {}))
        cropper-state (r/cursor state [:cropper])
        viewport-state (r/cursor cropper-state [:viewport])
        zoom-state (r/cursor cropper-state [:zoom])
        temp-img (new js/Image)
        _ (when-not uploaded?
            (set! (.-crossOrigin temp-img) "anonymous"))]
    (fn [{:keys [entity attribute img-src file-name target-width target-height cropper-class-name
                 round? on-done! save-always? close-modal!]}]
      (let [cropper-values @cropper-state
            zoom (or (:zoom cropper-values) 1)
            coordinates (or (:viewport cropper-values)
                            {:x 0 :y 0})
            img-src (or img-src
                        (get entity attribute))
            _ (set! (.-onload temp-img)
                    (fn []
                      (swap! cropper-state assoc :loaded? true)))
            _ (set! (.-onerror temp-img)
                    (fn [err]
                      ;; cross-origin error can be circumvented
                      ;; by doing cropping in backend, and cropper
                      ;;  will load normally
                      ;; => will show error message only if not properly loaded
                      ;;    after delay:
                      (js/setTimeout
                       (fn []
                         (when-not (:loaded? @cropper-state)
                           (swap! cropper-state assoc :error (loc/tr :failed-to-load-image))))
                       60000)))
            _ (when (not= (.-src temp-img) img-src)
                (swap! cropper-state assoc :loaded? false)
                (set! (.-src temp-img) img-src))
            {:keys [processing? loaded? error viewport]} cropper-values
            viewport-map (js->clj viewport :keywordize-keys true)
            coordinates-not-changed? (and (= 0 (:x viewport-map))
                                          (= 0 (:y viewport-map))) 
            zoom-not-changed? (= 1 zoom)
            no-changes? (and coordinates-not-changed?
                             zoom-not-changed?)]
        [:div
         {:class-name (fu/cs "image-cropper"
                             cropper-class-name)}
         [:div.image-cropper__container
          (cond
            error
            [:div.image-cropper__component
             [:div.image-cropper__info
              error]]
            :else
            [:div.image-cropper__component
             (when-not loaded?
               [:div.image-cropper__info
                [:i.fal.fa-spinner.fa-pulse.fa-2x]])
             [:> (aget js/ReactEasyCrop "default")
              (merge
               {:image             img-src
                :crop              coordinates 
                :min-zoom          0.5
                :zoom              zoom
                ;;:rotation (or @rotation 1)
                :show-grid         false
                :crop-shape        (if round?
                                     "round"
                                     "rect")
                :aspect            (/ target-width target-height)
                :on-media-loaded   (fn [img]
                                     (let [{:keys [naturalWidth naturalHeight]} (js->clj img :keywordize-keys true)]
                                       (when (and (or (< naturalWidth (/ target-width 2))
                                                      (< naturalHeight (/ target-height 2)))
                                                  (not (:loaded? @cropper-state)))
                                         (close-modal!)
                                         (modals/open-modal! zap-modal/modal-key
                                                             {:title   (loc/tr :image-too-small-title)
                                                              :color   ::color/danger
                                                              :content (modal-content
                                                                        {:title        (loc/tr :image-too-small-title)
                                                                         :content-text (loc/tr :image-too-small-info
                                                                                           [(/ target-width 2) (/ target-height 2)])})}))
                                       (if (and naturalWidth naturalHeight)
                                         (swap! cropper-state merge
                                                {:original {:width  naturalWidth
                                                            :height naturalHeight}
                                                 :error    nil
                                                 :loaded?  true})
                                         (swap! cropper-state assoc :error (loc/tr :failed-to-load-image)))))
                :on-crop-complete  (fn [area pixels]
                                     (let [pixels (js->clj pixels :keywordize-keys true)
                                           area (js->clj area :keywordize-keys true)
                                           crop-data {:pixels pixels :area area}
                                           cropped-image (try
                                                           (fu/crop-image
                                                            temp-img
                                                            pixels
                                                            target-width
                                                            target-height)
                                                           (catch :default e
                                                             ;;failed - likely cross-origin reasons
                                                             ;;=> cropping must be done in backend instead of
                                                             ;;   using base64 encoded image data directly
                                                             nil))
                                           image-data (when cropped-image
                                                        (str cropped-image))]
                                       (swap! cropper-state assoc
                                              :image-data image-data
                                              :pixels pixels
                                              :area area)))
                :on-crop-change    (fn [res]
                                     (reset! viewport-state res))}
               (when classes
                 {:classes classes}))]])
          [:div.zoom-section
           [:span.zoom-section__label.mr-3 (loc/tr :zoom-in)]
           [:input
            {:type       "range"
             :class-name "input-range"
             :disabled   (not loaded?)
             :min        0.5
             :max        5
             :step       0.05
             :value      zoom
             :on-change  (fn [res]
                           (let [zoom-value (.. res -target -value)]
                             (reset! zoom-state zoom-value)))}]
           [:span.zoom-section__label.ml-3 (loc/tr :zoom-out)]]
          ;;possibly in future:
          #_[:div
             "Rotation:"
             [:input
              {:type      "range"
               :min       0
               :max       360
               :step      1
               :value     (or @rotation 0)
               :on-change (fn [res]
                            (let [rot-value (.. res -target -value)]
                              (reset! rotation rot-value)))}]]]
         [:div.modal-footer.border-top.justify-content-start.gap-3
          [button/primary-solid
           {:disabled? (boolean (and (not save-always?)
                                     (or processing?
                                         error 
                                         (not loaded?)
                                         no-changes?)))
            :on-click  (fn [_]
                         ;;note: saving is hard coded for now - pass on-save! from outside if needed:
                         (when (and loaded?
                                    (not processing?))
                           (swap! cropper-state assoc
                                  :processing? true
                                  :error nil)
                           (let [pixels (get-in @state [:cropper :pixels])
                                 {:keys [x y width height]} pixels
                                 image-data (get-in @state [:cropper :image-data])
                                 original (get-in @state [:cropper :original])
                                 skip-update? (and (= (:width original)
                                                      width)
                                                   (= (:height original)
                                                      height)
                                                   (not save-always?))
                                 msg {:entity-id     (:db/id entity)
                                      :attribute     attribute
                                      :crop-x        x
                                      :crop-y        y
                                      :crop-width    width
                                      :crop-height   height
                                      :target-width  target-width
                                      :file-name     file-name
                                      :target-height target-height
                                      :image-data    image-data}]
                             ;;:rotation (or @rotation 0)

                             (if-not skip-update?
                               (do
                                 (common-actions/*->crop-image
                                  msg
                                  (fn [result]
                                    (when on-done!
                                      (on-done! (:body result)))
                                    (close-modal!))
                                  (fn [err]
                                    (swap! state dissoc :processing?)
                                    (logging/error "Image cropping failed" err))))
                               ;;no zooming or dragging: no need to save anything:
                               (do
                                 (when on-done!
                                   (on-done!))
                                 (close-modal!))))))}
           (if processing?
             (loc/tr :uploading)
             (loc/tr :save))]
          [button/secondary-hollow
           {:on-click (fn [_]
                        (close-modal!))}
           (loc/tr :cancel)]]]))))

(defn open-cropper-modal!
  [args]
  (modals/open-modal!
   dynamic-modal/modal-key
   {:title        (loc/tr :crop-image)
    :custom-class "image-cropper-modal"
    :render-fn    (fn [state close-modal!]
                    [view
                     (merge args
                            {:state        (or (:state args) state)
                             :close-modal! close-modal!})])}))
